import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiResponse } from '../../../_models/api-response.model';
import { ExcelExportService } from '../../../_services/app/excel-export.service'; 
import { PaginationComponent } from '../../../_shared_components/pagination/pagination.component';
import { AdminContactService } from '../../../_services/api/admin-contact.service';
import { Contact, ContactSearchCriteria } from '../../../_models/contact.model';
import { AdminContactCard } from '../admin-contact-card/admin-contact-card';

@Component({
  selector: 'app-admin-contacts-list',
  imports: [CommonModule, FormsModule, AdminContactCard, PaginationComponent],
  templateUrl: './admin-contacts-list.html',
  styleUrl: './admin-contacts-list.scss',
})
export class AdminContactsList implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  selectedCategory: string = 'all';

  contacts: Contact[] = [];
  archivedList = false;
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedVisibility: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private contactService: AdminContactService, 
    private excelExportService: ExcelExportService, 
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadContacts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadContacts();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadContacts();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadContacts();
  }

  loadContacts(): void {
    this.isLoading = true;
    
    const criteria: ContactSearchCriteria = {};
    criteria.isArchived = false;
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.isRead = this.selectedVisibility === 'true';
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.contactService.getAllContacts(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.contacts = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading contacts:', error);
          this.isLoading = false;
        }
      });
  }

  onContactUpdated(): void {
    if(this.archivedList){
      this.loadArchivedContacts();
    }else{
      this.loadContacts();
    }
  }

  onContactDeleted(): void {
    if(this.archivedList){
      this.loadArchivedContacts();
    }else{
      this.loadContacts();
    }
  }

  onContactArchived(): void {
    if(this.archivedList){
      this.loadArchivedContacts();
    }else{
      this.loadContacts();
    }
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }
    setTimeout(() => {
      this.loadContacts();
    }, 1000);
  }

  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  getArchiveContact(){
    this.isLoading = true;    
    this.archivedList = !this.archivedList;
    if(this.archivedList){
      this.loadArchivedContacts();
    }else{
      this.loadContacts();
    }
  }

  private loadArchivedContacts(){
    const criteria: ContactSearchCriteria = {};
      criteria.isArchived = true;
      const [sortField, sortDirection] = this.selectedSort.split(',');
      this.contactService.getAllContacts(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
        .subscribe({
          next: (response: ApiResponse<any>) => {
            this.contacts = response.data?.content || [];
            this.totalCount = response.data?.totalElements || 0;
            this.totalPages = response.data?.totalPages || 0;
            setTimeout(() => {
              this.isLoading = false;
            }, 500);
          },
          error: (error) => {
            console.error('Error loading contacts:', error);
            this.isLoading = false;
          }
        });
  }
  
}